import { motion } from 'motion/react';

import type { Finding, Pillar } from '@smartapply/shared';
import { PILLAR_LABELS } from '@smartapply/shared';

const PILLAR_ORDER: ReadonlyArray<Pillar> = ['views', 'interactions', 'visits', 'ats'];

const PILLAR_ACCENTS: Record<Pillar, string> = {
  views: 'var(--accent-saffron)',
  interactions: 'var(--accent-crimson)',
  visits: 'var(--accent-forest)',
  ats: 'var(--ink-primary)',
};

interface PillarsBoardProps {
  findings: Finding[];
}

interface Bucket {
  total: number;
  critical: number;
  suggestion: number;
}

function bucketsByPillar(findings: Finding[]): Record<Pillar, Bucket> {
  const out: Record<Pillar, Bucket> = {
    views: { total: 0, critical: 0, suggestion: 0 },
    interactions: { total: 0, critical: 0, suggestion: 0 },
    visits: { total: 0, critical: 0, suggestion: 0 },
    ats: { total: 0, critical: 0, suggestion: 0 },
  };
  for (const f of findings) {
    const pillars = f.affectedPillars ?? ['views'];
    for (const p of pillars) {
      const bucket = out[p];
      if (!bucket) continue;
      bucket.total += 1;
      if (f.severity === 'critical') bucket.critical += 1;
      if (f.severity === 'suggestion') bucket.suggestion += 1;
    }
  }
  return out;
}

export function PillarsBoard({ findings }: PillarsBoardProps) {
  const buckets = bucketsByPillar(findings);

  return (
    <section className="mt-10">
      <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--ink-tertiary)]">
        impacto nos 4 pilares do produto
      </p>
      <p className="mt-1 font-sans text-xs italic text-[var(--ink-tertiary)]">
        Cada finding deste diagnóstico melhora pelo menos um dos pilares abaixo. Use o placar pra
        priorizar o que endereçar primeiro.
      </p>
      <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {PILLAR_ORDER.map((pillar, i) => {
          const meta = PILLAR_LABELS[pillar];
          const b = buckets[pillar];
          return (
            <motion.div
              key={pillar}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25, delay: i * 0.05, ease: 'easeOut' }}
              className="card-paper rounded-sm px-4 py-4"
              style={{ borderLeft: `3px solid ${PILLAR_ACCENTS[pillar]}` }}
            >
              <p
                className="font-mono text-[10px] uppercase tracking-[0.18em]"
                style={{ color: PILLAR_ACCENTS[pillar] }}
              >
                pilar · {meta.label.toLowerCase()}
              </p>
              <p className="mt-2 font-serif text-2xl font-medium tabular-nums text-[var(--ink-primary)]">
                {b.total}
                <span className="ml-1 font-sans text-xs font-normal italic text-[var(--ink-tertiary)]">
                  {b.total === 1 ? 'finding' : 'findings'}
                </span>
              </p>
              <p className="mt-1 font-mono text-[11px] tabular-nums text-[var(--ink-secondary)]">
                {b.critical > 0 && (
                  <span className="mr-2">
                    <span style={{ color: 'var(--accent-crimson)' }}>{b.critical}</span> crítico
                    {b.critical === 1 ? '' : 's'}
                  </span>
                )}
                {b.suggestion > 0 && (
                  <span>
                    <span style={{ color: 'var(--ink-secondary)' }}>{b.suggestion}</span> sugest
                    {b.suggestion === 1 ? 'ão' : 'ões'}
                  </span>
                )}
                {b.total === 0 && (
                  <span className="italic text-[var(--ink-tertiary)]">nenhum ponto crítico</span>
                )}
              </p>
              <p className="mt-3 font-sans text-xs leading-relaxed text-[var(--ink-secondary)]">
                {meta.description}
              </p>
            </motion.div>
          );
        })}
      </div>
    </section>
  );
}
